import { useEffect, useMemo, useState } from "react";
import { Download, Eye, FileCheck2, History, Search, ShieldCheck, X } from "lucide-react";
import {
  downloadCanonicalStatementVersion,
  getCanonicalStatementDownloadDetail,
  getCanonicalStatementDownloadReports,
  listCanonicalStatementDownloads,
  previewCanonicalStatementVersionUrl,
  verifyCanonicalStatementVersion,
  type CanonicalStatementDocumentVersionRecord,
  type CanonicalStatementDownloadDetailResponse,
  type CanonicalStatementDownloadPermissions,
  type CanonicalStatementDownloadQueueItem,
  type CanonicalStatementDownloadReports,
} from "../../lib/arRecoveryStatementsApi";

type StatementDownloadWorkspaceProps = {
  onClose: () => void;
  onOpenPrint?: (statementId: string) => void;
  onOpenSend?: (statementId: string) => void;
  initialStatementId?: string;
  initialClient?: string;
  initialCustomer?: string;
  initialAccount?: string;
  initialRecipient?: string;
  sourceWorkspace?: string;
};

const EMPTY_PERMISSIONS: CanonicalStatementDownloadPermissions = {
  canView: false,
  canDownload: false,
  canDownloadHistorical: false,
  canViewAudit: false,
  canVerifyIntegrity: false,
};

export function ARRecoveryStatementDownloadWorkspace({
  onClose,
  onOpenPrint,
  onOpenSend,
  initialStatementId = "",
  initialClient = "",
  initialCustomer = "",
  initialAccount = "",
  initialRecipient = "",
  sourceWorkspace = "Statement Center",
}: StatementDownloadWorkspaceProps) {
  const [search, setSearch] = useState(initialStatementId);
  const [client, setClient] = useState(initialClient);
  const [customer, setCustomer] = useState(initialCustomer);
  const [account, setAccount] = useState(initialAccount);
  const [recipient, setRecipient] = useState(initialRecipient);
  const [status, setStatus] = useState("");
  const [version, setVersion] = useState("");
  const [generatedDate, setGeneratedDate] = useState("");
  const [downloadedDate, setDownloadedDate] = useState("");
  const [downloadStatus, setDownloadStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });
  const [items, setItems] = useState<CanonicalStatementDownloadQueueItem[]>([]);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [selectedStatementId, setSelectedStatementId] = useState(initialStatementId);
  const [detail, setDetail] = useState<CanonicalStatementDownloadDetailResponse | null>(null);
  const [reports, setReports] = useState<CanonicalStatementDownloadReports | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [busyVersionId, setBusyVersionId] = useState("");
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    setPage(1);
  }, [search, client, customer, account, recipient, status, version, generatedDate, downloadedDate, downloadStatus]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      let cancelled = false;
      setIsLoading(true);
      setError("");
      listCanonicalStatementDownloads(null, {
        search,
        client,
        customer,
        account,
        recipient,
        status,
        version,
        generatedDate,
        downloadedDate,
        downloadStatus,
        page,
        pageSize,
      })
        .then((payload) => {
          if (cancelled) return;
          setItems(payload.items);
          setPermissions(payload.permissions);
          setPagination(payload.pagination);
          setSelectedStatementId((current) => {
            if (current && payload.items.some((item) => item.statementId === current)) return current;
            return payload.items[0]?.statementId || "";
          });
        })
        .catch((nextError) => {
          if (!cancelled) {
            setItems([]);
            setPermissions(EMPTY_PERMISSIONS);
            setError(nextError instanceof Error ? nextError.message : "Statement Download queue could not be loaded.");
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, 220);
    return () => window.clearTimeout(timeoutId);
  }, [search, client, customer, account, recipient, status, version, generatedDate, downloadedDate, downloadStatus, page, pageSize]);

  useEffect(() => {
    if (!selectedStatementId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setIsDetailLoading(true);
    setActionMessage("");
    getCanonicalStatementDownloadDetail(selectedStatementId)
      .then((payload) => {
        if (!cancelled) {
          setDetail(payload);
          setPermissions(payload.permissions);
        }
      })
      .catch((nextError) => {
        if (!cancelled) {
          setDetail(null);
          setError(nextError instanceof Error ? nextError.message : "Statement Download detail could not be loaded.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedStatementId]);

  useEffect(() => {
    if (!permissions.canViewAudit) {
      setReports(null);
      return;
    }
    getCanonicalStatementDownloadReports().then(setReports).catch(() => setReports(null));
  }, [permissions.canViewAudit]);

  const latestVersion = useMemo(
    () => detail?.versions.find((item) => item.isActive) || detail?.versions[0] || null,
    [detail],
  );

  async function downloadVersion(versionRecord: CanonicalStatementDocumentVersionRecord) {
    if (!detail || busyVersionId) return;
    setBusyVersionId(versionRecord.documentVersionId);
    setActionMessage("");
    try {
      const result = await downloadCanonicalStatementVersion(detail.statement.statementId, versionRecord.documentVersionId, {
        sourceWorkspace,
        correlationId: crypto.randomUUID(),
      });
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setActionMessage(`Version ${versionRecord.versionNumber} downloaded successfully.`);
      const refreshed = await getCanonicalStatementDownloadDetail(detail.statement.statementId);
      setDetail(refreshed);
      const queue = await listCanonicalStatementDownloads(null, { search, client, customer, account, recipient, status, version, generatedDate, downloadedDate, downloadStatus, page, pageSize });
      setItems(queue.items);
      setPagination(queue.pagination);
    } catch (nextError) {
      setActionMessage(nextError instanceof Error ? nextError.message : "Statement download could not be completed.");
    } finally {
      setBusyVersionId("");
    }
  }

  async function verifyVersion(versionRecord: CanonicalStatementDocumentVersionRecord) {
    if (!detail || busyVersionId) return;
    setBusyVersionId(versionRecord.documentVersionId);
    setActionMessage("");
    try {
      const result = await verifyCanonicalStatementVersion(detail.statement.statementId, versionRecord.documentVersionId, {
        sourceWorkspace,
        correlationId: crypto.randomUUID(),
      });
      setActionMessage(`Version ${result.integrity.versionNumber} integrity verified at ${formatDateTime(result.integrity.checkedAt)}.`);
      setDetail(await getCanonicalStatementDownloadDetail(detail.statement.statementId));
    } catch (nextError) {
      setActionMessage(nextError instanceof Error ? nextError.message : "Integrity verification could not be completed.");
    } finally {
      setBusyVersionId("");
    }
  }

  function previewVersion(versionRecord: CanonicalStatementDocumentVersionRecord) {
    if (!detail || busyVersionId) return;
    const url = previewCanonicalStatementVersionUrl(detail.statement.statementId, versionRecord.documentVersionId, {
      sourceWorkspace,
      correlationId: crypto.randomUUID(),
    });
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function clearFilters() {
    setSearch("");
    setClient("");
    setCustomer("");
    setAccount("");
    setRecipient("");
    setStatus("");
    setVersion("");
    setGeneratedDate("");
    setDownloadedDate("");
    setDownloadStatus("");
    setPage(1);
  }

  return (
    <div className="mt-3 grid min-w-0 max-w-full gap-3 overflow-hidden text-[#0A2540]" data-statement-download-workspace="canonical-r2">
      <section className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#14B8A6]">Statement Operations</p>
            <h1 className="mt-1 break-words font-display text-[1.65rem] font-bold leading-tight">Statement Download</h1>
            <p className="mt-1 max-w-4xl text-sm font-semibold leading-5 text-[#475569]">
              Securely retrieve certified immutable PDF versions with tenant authorization, integrity validation, and complete audit history.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {onOpenSend && selectedStatementId ? (
              <button type="button" onClick={() => onOpenSend(selectedStatementId)} className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white">
                Send Now
              </button>
            ) : null}
            {onOpenPrint && selectedStatementId ? (
              <button type="button" onClick={() => onOpenPrint(selectedStatementId)} className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#14B8A6] bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em]">
                Open Statement Print
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.12em]">
              <X className="size-4" aria-hidden="true" /> Close
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-2.5 sm:grid-cols-2 xl:grid-cols-5">
        <DownloadMetric label="Available statements" value={pagination.total} />
        <DownloadMetric label="Downloaded statements" value={reports?.summary.statementsDownloaded ?? items.filter((item) => item.downloadCount > 0).length} />
        <DownloadMetric label="Never downloaded" value={reports?.summary.neverDownloaded ?? items.filter((item) => item.documentVersionId && item.downloadCount === 0).length} />
        <DownloadMetric label="Historical downloads" value={reports?.summary.historicalVersionDownloads ?? 0} />
        <DownloadMetric label="Failed downloads" value={reports?.summary.failedDownloads ?? 0} />
      </section>

      <section className="grid min-w-0 gap-3 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-3.5 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
        <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(15rem,2fr)_repeat(4,minmax(8rem,1fr))]">
          <label className="relative min-w-0">
            <span className="sr-only">Search Statement downloads</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
            <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Statement, customer, account, client, recipient" className="focus-ring w-full rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] py-2.5 pl-10 pr-3 text-sm font-semibold" />
          </label>
          <input value={client} onChange={(event) => setClient(event.target.value)} placeholder="Client" aria-label="Filter downloads by client" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input value={customer} onChange={(event) => setCustomer(event.target.value)} placeholder="Customer" aria-label="Filter downloads by customer" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input value={account} onChange={(event) => setAccount(event.target.value)} placeholder="Account / placement" aria-label="Filter downloads by account or placement" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="Recipient" aria-label="Filter downloads by recipient" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
        </div>
        <div className="grid min-w-0 gap-2 sm:grid-cols-2 lg:grid-cols-[repeat(5,minmax(8rem,1fr))_auto]">
          <input value={status} onChange={(event) => setStatus(event.target.value)} placeholder="Statement status" aria-label="Filter by Statement status" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input value={version} onChange={(event) => setVersion(event.target.value)} placeholder="Version" aria-label="Filter by version" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input type="date" value={generatedDate} onChange={(event) => setGeneratedDate(event.target.value)} aria-label="Filter by generated date" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <input type="date" value={downloadedDate} onChange={(event) => setDownloadedDate(event.target.value)} aria-label="Filter by downloaded date" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold" />
          <select value={downloadStatus} onChange={(event) => setDownloadStatus(event.target.value)} aria-label="Filter by download status" className="focus-ring min-w-0 rounded-xl border border-[#BFEAFF] px-3 py-2.5 text-sm font-semibold">
            <option value="">All download states</option>
            <option value="Downloaded">Downloaded</option>
            <option value="Never Downloaded">Never downloaded</option>
          </select>
          <button type="button" onClick={clearFilters} className="focus-ring rounded-xl border border-[#BFEAFF] bg-white px-4 py-2.5 text-xs font-black uppercase tracking-[0.12em]">Clear</button>
        </div>

        {error ? <DownloadNotice tone="error">{error}</DownloadNotice> : null}
        <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-[#BFEAFF]">
          <table className="w-full min-w-[78rem] border-collapse text-left text-sm">
            <thead className="bg-[#F4FBFF] text-[10px] font-black uppercase tracking-[0.11em]">
              <tr>{["Client", "Customer", "Statement Number", "Statement Date", "Version", "Status", "Generated", "Downloaded", "Last Downloaded"].map((heading) => <th key={heading} className="border-b border-[#BFEAFF] px-3 py-3">{heading}</th>)}</tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const selected = item.statementId === selectedStatementId;
                return (
                  <tr
                    key={item.statementId}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedStatementId(item.statementId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedStatementId(item.statementId);
                      }
                    }}
                    className={`cursor-pointer border-b border-[#E0F2FE] ${selected ? "bg-[#ECFEFF]" : "bg-white hover:bg-[#F8FDFF]"}`}
                  >
                    <td className="px-3 py-3 font-semibold">{item.clientCompany || "Not captured"}</td>
                    <td className="px-3 py-3 font-bold">{item.customerName}</td>
                    <td className="px-3 py-3 font-black">{item.statementNumber}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{formatDate(item.statementDate)}</td>
                    <td className="px-3 py-3 font-bold">{item.versionNumber ? `v${item.versionNumber}` : "Unavailable"}</td>
                    <td className="px-3 py-3 font-semibold">{item.documentStatus}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{formatDateTime(item.generatedAt)}</td>
                    <td className="px-3 py-3 font-bold">{item.downloadCount}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{formatDateTime(item.lastDownloadedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!items.length ? <p className="p-5 text-sm font-semibold text-[#475569]">{isLoading ? "Loading Statement downloads..." : "No Statements matched the download filters."}</p> : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-black uppercase tracking-[0.12em] text-[#475569]">
          <span>Page {pagination.page} of {pagination.totalPages} / {pagination.total} Statement records</span>
          <div className="flex flex-wrap items-center gap-2">
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} aria-label="Statement Download page size" className="focus-ring rounded-full border border-[#BFEAFF] bg-white px-3 py-2">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <button type="button" disabled={pagination.page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="focus-ring rounded-full border border-[#BFEAFF] px-4 py-2 disabled:cursor-not-allowed disabled:opacity-45">Previous</button>
            <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage((current) => current + 1)} className="focus-ring rounded-full border border-[#BFEAFF] px-4 py-2 disabled:cursor-not-allowed disabled:opacity-45">Next</button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <article className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#0F766E]">Detail Workspace</p>
          {isDetailLoading ? <p className="mt-3 text-sm font-semibold text-[#475569]">Loading canonical document detail...</p> : detail ? (
            <>
              <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="break-words font-display text-xl font-bold">{detail.statement.statementNumber}</h2>
                  <p className="mt-1 text-sm font-semibold text-[#475569]">{detail.statement.customerName} / {detail.statement.accountId}</p>
                </div>
                {latestVersion ? (
                  <button
                    type="button"
                    disabled={!permissions.canDownload || Boolean(busyVersionId)}
                    onClick={() => downloadVersion(latestVersion)}
                    className="focus-ring inline-flex items-center gap-2 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Download className="size-4" aria-hidden="true" /> Download Latest
                  </button>
                ) : null}
              </div>
              <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
                <DetailField label="Statement information" value={`${detail.statement.statementType || "Account Statement"} / ${formatDate(detail.statement.statementDate)}`} />
                <DetailField label="Customer" value={`${detail.statement.customerName} / ${detail.statement.customerNumber}`} />
                <DetailField label="Account" value={detail.statement.accountId} />
                <DetailField label="Placement" value="Not captured in the canonical Statement schema" />
                <DetailField label="Recipient information" value={`${detail.statement.recipientName || "Not captured"} / ${detail.statement.recipientEmail || "Not captured"}`} />
                <DetailField label="Generation information" value={latestVersion ? `${formatDateTime(latestVersion.generatedAt)} / v${latestVersion.versionNumber}` : "No immutable version"} />
                <DetailField label="Storage information" value={latestVersion ? `${latestVersion.storageProvider || "Unknown"} / ${latestVersion.storageKeyPresent ? "Key recorded" : "Key missing"}` : "Unavailable"} />
                <DetailField label="Document classification" value={latestVersion?.legacyClassification || "UNKNOWN"} />
                <DetailField label="Integrity" value={latestVersion?.contentHash ? "SHA-256 checksum recorded" : "Checksum not recorded"} />
              </div>
              {actionMessage ? <DownloadNotice tone={/failed|missing|denied|could not|unavailable/i.test(actionMessage) ? "error" : "success"}>{actionMessage}</DownloadNotice> : null}
            </>
          ) : <p className="mt-3 text-sm font-semibold text-[#475569]">Select a Statement to review its download detail.</p>}
        </article>

        <article className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4 shadow-[0_12px_30px_rgba(10,37,64,.07)]">
          <div className="flex items-center gap-2">
            <History className="size-4 text-[#14B8A6]" aria-hidden="true" />
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#0F766E]">Version History</p>
          </div>
          <div className="mt-3 grid gap-2">
            {detail?.versions.map((versionRecord) => {
              const historicalBlocked = !versionRecord.isActive && !permissions.canDownloadHistorical;
              return (
                <article key={versionRecord.documentVersionId} className="grid min-w-0 gap-3 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-lg font-bold">Version {versionRecord.versionNumber}</p>
                      <span className="rounded-full border border-[#BFEAFF] bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em]">{versionRecord.isActive ? "Active" : versionRecord.documentStatus}</span>
                    </div>
                    <p className="mt-1 break-words text-sm font-semibold text-[#475569]">
                      Created {formatDateTime(versionRecord.generatedAt || versionRecord.createdAt)} / {formatBytes(versionRecord.fileSize)}
                    </p>
                    <p className="mt-1 break-words text-xs font-semibold text-[#475569]">
                      Reason: {versionRecord.correctionReason || "Initial certified generation"} / Superseded by: {versionRecord.supersededByDocumentVersionId || "None"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={!permissions.canDownload || historicalBlocked || Boolean(busyVersionId)}
                      onClick={() => previewVersion(versionRecord)}
                      className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <Eye className="size-3.5" aria-hidden="true" /> Preview
                    </button>
                    <button
                      type="button"
                      disabled={!permissions.canDownload || historicalBlocked || Boolean(busyVersionId)}
                      onClick={() => downloadVersion(versionRecord)}
                      className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-[#14B8A6] bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <Download className="size-3.5" aria-hidden="true" /> Download
                    </button>
                    <button
                      type="button"
                      disabled={!permissions.canVerifyIntegrity || Boolean(busyVersionId)}
                      onClick={() => verifyVersion(versionRecord)}
                      className="focus-ring inline-flex items-center gap-1.5 rounded-full border border-[#BFEAFF] bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.1em] disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <ShieldCheck className="size-3.5" aria-hidden="true" /> Verify
                    </button>
                  </div>
                </article>
              );
            })}
            {!detail?.versions.length ? <p className="rounded-xl border border-dashed border-[#BFEAFF] p-4 text-sm font-semibold text-[#475569]">No immutable Statement versions are available.</p> : null}
          </div>
        </article>
      </section>

      <section className="grid min-w-0 gap-3 lg:grid-cols-2">
        <article className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4">
          <div className="flex items-center gap-2">
            <FileCheck2 className="size-4 text-[#14B8A6]" aria-hidden="true" />
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#0F766E]">Audit History</p>
          </div>
          {permissions.canViewAudit ? (
            <div className="mt-3 grid gap-2">
              {detail?.audit.slice(0, 12).map((entry) => (
                <div key={entry.auditId} className="grid min-w-0 gap-1 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3 sm:grid-cols-[9rem_minmax(0,1fr)]">
                  <p className="text-[11px] font-black uppercase tracking-[0.1em] text-[#0F766E]">{formatDateTime(entry.occurredAt)}</p>
                  <div className="min-w-0">
                    <p className="break-words text-sm font-bold">{entry.eventType} / v{entry.versionNumber || "?"}</p>
                    <p className="break-words text-xs font-semibold text-[#475569]">{entry.actorName || "System"} / {entry.sourceWorkspace} / {entry.resultStatus}</p>
                    <p
                      className="mt-1 break-all font-mono text-[10px] font-semibold text-[#64748B] selection:bg-[#CCFBF1]"
                      title={`Correlation ID: ${entry.correlationId}`}
                    >
                      Correlation: <span className="select-all">{entry.correlationId}</span>
                    </p>
                  </div>
                </div>
              ))}
              {!detail?.audit.length ? <p className="text-sm font-semibold text-[#475569]">No Statement download audit events recorded.</p> : null}
            </div>
          ) : <p className="mt-3 text-sm font-semibold text-[#475569]">Statement audit permission is required.</p>}
        </article>
        <article className="min-w-0 rounded-[1.35rem] border border-[#BFEAFF] bg-white p-4">
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#0F766E]">Operational Reporting</p>
          {permissions.canViewAudit && reports ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ReportList title="Most downloaded Statements" items={reports.mostDownloadedStatements.map((item) => ({ label: `${item.statement_number} / ${item.customer_name}`, value: Number(item.download_count || 0) }))} />
              <ReportList title="Never downloaded" items={reports.neverDownloadedStatements.map((item) => ({ label: `${item.statement_number} / ${item.customer_name}`, value: "Ready" }))} />
              <ReportList title="Downloads by user" items={reports.downloadsByUser.map((item) => ({ label: item.actor_name || "Unknown user", value: Number(item.download_count || 0) }))} />
              <ReportList title="Downloads by client" items={reports.downloadsByClient.map((item) => ({ label: item.client_company || "Unassigned client", value: Number(item.download_count || 0) }))} />
              <ReportList title="Failed downloads" items={reports.failedDownloads.map((item) => ({ label: `${item.statement_id} / v${item.version_number || "?"}`, value: formatDateTime(item.occurred_at) }))} />
              <ReportList title="Permission profile" items={[
                { label: "Download Statements", value: permissions.canDownload ? "Allowed" : "Denied" },
                { label: "Historical Versions", value: permissions.canDownloadHistorical ? "Allowed" : "Denied" },
                { label: "Verify Integrity", value: permissions.canVerifyIntegrity ? "Allowed" : "Denied" },
              ]} />
            </div>
          ) : <p className="mt-3 text-sm font-semibold text-[#475569]">Operational download reporting requires Statement audit permission.</p>}
        </article>
      </section>
    </div>
  );
}

function DownloadMetric({ label, value }: { label: string; value: number }) {
  return (
    <article className="min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3.5 py-3 shadow-[0_8px_20px_rgba(10,37,64,.045)]">
      <p className="text-[10px] font-black uppercase tracking-[0.13em] text-[#0F766E]">{label}</p>
      <p className="mt-1 font-display text-[1.45rem] font-bold leading-tight">{value}</p>
    </article>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3">
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold leading-5">{value}</p>
    </div>
  );
}

function DownloadNotice({ children, tone }: { children: React.ReactNode; tone: "success" | "error" }) {
  return (
    <p className={`mt-3 rounded-xl border px-4 py-3 text-sm font-semibold ${tone === "success" ? "border-[#14B8A6] bg-[#ECFEFF] text-[#0F766E]" : "border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]"}`}>
      {children}
    </p>
  );
}

function ReportList({ title, items }: { title: string; items: Array<{ label: string; value: string | number }> }) {
  return (
    <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F8FDFF] p-3">
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#0F766E]">{title}</p>
      <div className="mt-2 grid gap-1.5">
        {items.slice(0, 8).map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex min-w-0 items-start justify-between gap-3 text-xs font-semibold">
            <span className="min-w-0 break-words text-[#475569]">{item.label}</span>
            <span className="shrink-0 font-black text-[#0A2540]">{item.value}</span>
          </div>
        ))}
        {!items.length ? <p className="text-xs font-semibold text-[#475569]">No records.</p> : null}
      </div>
    </div>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "Not captured";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-US", { dateStyle: "medium" });
}

function formatDateTime(value?: string | null) {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function formatBytes(value?: number | null) {
  if (!value) return "Size not captured";
  if (value < 1024) return `${value} bytes`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
